import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, DownloadIcon, LockIcon, XIcon } from 'lucide-react';
import type { PulseResponse } from '../../types/pulse';
import { storyboard, type StoryboardFrame } from '../../data/storyboard';
import { followUps, teamRating, techRating } from '../../data/pulse';
import { deriveAggregates } from '../../utils/pulse';
import { OpeningScreen } from './screens/OpeningScreen';
import { RankScreen } from './screens/RankScreen';
import { RankResultScreen } from './screens/RankResultScreen';
import { FollowUpScreen } from './screens/FollowUpScreen';
import { FollowUpResultScreen } from './screens/FollowUpResultScreen';
import { RatingScreen } from './screens/RatingScreen';
import { RatingResultScreen } from './screens/RatingResultScreen';
import { OpenScreen } from './screens/OpenScreen';
import { OpenResultScreen } from './screens/OpenResultScreen';
import { SnapshotScreen } from './screens/SnapshotScreen';
interface StoryboardOverlayProps {
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** A representative completed response, so every frame shows real content. */
const demo: PulseResponse = {
  ranking: ['treatment', 'followup', 'diagnosis'],
  topArea: 'treatment',
  followUpChoice: 'burden',
  followUpFreeText: '',
  teamRating: 4,
  techRating: 4,
  openResponse: 'Less treatment burden so we can spend specialist time where it counts',
  submittedAt: '2026-09-18T10:00:00.000Z'
};
const noop = () => {};
function FrameScreen({
  id
}: {
  id: string;
}) {
  const aggregates = deriveAggregates(demo);
  switch (id) {
    case 'opening':
      return <OpeningScreen data-id="element-329" data-mp="31ff60hdl9iqc" />;
    case 'q1':
      return <RankScreen value={['treatment', 'followup', 'diagnosis']} onChange={noop} data-id="element-330" data-mp="2wh3um27nb060" />;
    case 'q1r':
      return <RankResultScreen topArea="treatment" aggregates={aggregates} data-id="element-331" data-mp="3agd82u4gvg6a" />;
    case 'q2':
      return <FollowUpScreen area="treatment" selected="burden" onSelect={noop} freeText="" onFreeText={noop} data-id="element-332" data-mp="35i21qtddrnst" />;
    case 'q2-diagnosis':
      return <FollowUpScreen area="diagnosis" selected={null} onSelect={noop} freeText="" onFreeText={noop} data-id="element-333" data-mp="282fhf19zl5l3" />;
    case 'q2-followup':
      return <FollowUpScreen area="followup" selected={null} onSelect={noop} freeText="" onFreeText={noop} data-id="element-334" data-mp="2gwkazsgi8v4b" />;
    case 'q2r':
      return <FollowUpResultScreen area="treatment" choice="burden" freeText="" aggregates={aggregates} data-id="element-335" data-mp="2qsnasrnvwho3" />;
    case 'q3':
      return <RatingScreen block={teamRating} value={4} onChange={noop} data-id="element-336" data-mp="2zuwmvlyswj3l" />;
    case 'q3r':
      return <RatingResultScreen value={4} tally={aggregates.team} context="Better use of the wider eye-care team" data-id="element-337" data-mp="11fa3ctkh6dir" />;
    case 'q4':
      return <RatingScreen block={techRating} value={4} onChange={noop} data-id="element-338" data-mp="xwuk80cvw5pr" />;
    case 'q4r':
      return <RatingResultScreen value={4} tally={aggregates.tech} context="Potential of technology to free specialist time" data-id="element-339" data-mp="t64g0qg728wn" />;
    case 'q5':
      return <OpenScreen value={demo.openResponse} onChange={noop} aggregates={aggregates} data-id="element-340" data-mp="o4oqom6zhg8g" />;
    case 'q5r':
      return <OpenResultScreen aggregates={aggregates} answer={demo.openResponse} data-id="element-341" data-mp="1gvqaj0lmfzyb" />;
    case 'snapshot':
      return <SnapshotScreen aggregates={aggregates} onRestart={noop} onOpenData={noop} data-id="element-342" data-mp="1bwvkpep8lb4p" />;
    default:
      return null;
  }
}
function Frame({
  frame,
  index
}: {
  frame: StoryboardFrame;
  index: number;
}) {
  return <article className={`sb-frame flex flex-col gap-6 border-t border-line pt-7 sm:flex-row ${index === 0 ? 'sb-frame-first' : ''}`} data-id="element-343" data-mp="12wgx28osvjy1">
      <div className="shrink-0" data-id="element-344" data-mp="3sj6icxq9maid">
        <div className="pointer-events-none flex h-[560px] w-[318px] flex-col overflow-hidden rounded-[26px] border border-line bg-paper px-5 py-5" aria-hidden="true" data-id="element-345" data-mp="3nmg4mh1xnr04">
          <FrameScreen id={frame.id} data-id="element-346" data-mp="1k3x29mfcgqhk" />
        </div>
        <div className="mt-2.5 flex w-[318px] items-center justify-center gap-2 rounded-full bg-navy px-4 py-2.5" data-id="element-347" data-mp="1k5huh5e6rxc9">
          <span className="text-[12px] font-semibold text-paper" data-id="element-348" data-mp="16apa9tncpszc">{frame.cta}</span>
          <ArrowRightIcon className="h-3 w-3 text-mint" aria-hidden="true" data-id="element-349" data-mp="1aen7tzx12vqa" />
        </div>
      </div>

      <div className="min-w-0 flex-1" data-id="element-350" data-mp="1y4r8byex6fd2">
        <div className="flex items-baseline justify-between gap-3" data-id="element-351" data-mp="232srskg9cgp2">
          <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-352" data-mp="230yg7wqvm0xn">{frame.page}</p>
          {frame.variant && <span className="rounded-full border border-line px-2 py-0.5 text-[8.5px] uppercase tracking-micro text-mut" data-id="element-353" data-mp="1u2cqnhmzllnd">
              Split route
            </span>}
        </div>
        <h3 className="mt-2 font-display text-[20px] font-semibold leading-tight tracking-tight text-navy" data-id="element-354" data-mp="dqh55ijo2den">
          {frame.title}
        </h3>
        <p className="mt-2 text-[10px] uppercase tracking-micro text-mut" data-id="element-355" data-mp="a55pn5fvlozb">{frame.cue}</p>

        <p className="mt-4 text-[12.5px] leading-relaxed text-navy2" data-id="element-356" data-mp="65da28s2eikg">{frame.purpose}</p>

        {frame.logic && <div className="mt-4 rounded-xl border border-line bg-mist px-4 py-3" data-id="element-357" data-mp="k31yfqlcc7u9">
            <p className="text-[9px] uppercase tracking-micro text-mut" data-id="element-358" data-mp="f50fr6rtuajb">Logic</p>
            <p className="mt-1.5 text-[12px] leading-relaxed text-navy2" data-id="element-359" data-mp="3nktsrbmy93vm">{frame.logic}</p>
          </div>}

        <p className="mt-4 text-[11px] leading-snug text-mut" data-id="element-360" data-mp="3ims2o6r3w4an">
          CTA: <span className="font-medium text-navy" data-id="element-361" data-mp="15imof7i0b45">{frame.cta}</span>
        </p>
      </div>
    </article>;
}
export function StoryboardOverlay({
  onClose
}: StoryboardOverlayProps) {
  useEffect(() => {
    const done = () => document.body.classList.remove('printing-storyboard');
    window.addEventListener('afterprint', done);
    return () => {
      window.removeEventListener('afterprint', done);
      done();
    };
  }, []);
  const download = () => {
    document.body.classList.add('printing-storyboard');
    window.setTimeout(() => window.print(), 60);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="storyboard-overlay fixed inset-0 z-50 overflow-y-auto bg-navy/45 px-4 py-8 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label={storyboard.title} data-id="element-362" data-mp="18pct4smi8s3j">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 8
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className="storyboard-sheet mx-auto w-full max-w-3xl overflow-hidden rounded-[24px] bg-paper shadow-soft" data-id="element-363" data-mp="prtjpnw4x7gl">
        <header className="flex items-start justify-between gap-4 bg-navy px-7 py-6" data-id="element-364" data-mp="258bul8mtzlaa">
          <div className="min-w-0" data-id="element-365" data-mp="2a7naygjacorc">
            <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-micro text-mint" data-id="element-366" data-mp="2a6lozh0qehuu">
              <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-367" data-mp="2kblkfkge2b9i" />
              {storyboard.eyebrow}
            </p>
            <h2 className="mt-2.5 font-display text-[22px] font-semibold leading-tight tracking-tight text-paper" data-id="element-368" data-mp="2ffmrdjk4852r">
              {storyboard.title}
            </h2>
            <p className="mt-2.5 max-w-xl text-[11.5px] leading-relaxed text-sky" data-id="element-369" data-mp="26k73dj61kk53">
              {storyboard.intro}
            </p>
          </div>
          <div className="no-print flex shrink-0 items-center gap-2" data-id="element-370" data-mp="1wmsnplrxzzdu">
            <button type="button" onClick={download} className="flex h-9 items-center gap-2 rounded-full bg-mint px-4 text-[12px] font-semibold text-navy transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-navy" data-id="element-371" data-mp="1wlhjjkul4pm7">
              <DownloadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-372" data-mp="3i0namp7fzugv" />
              {storyboard.download}
            </button>
            <button type="button" onClick={onClose} aria-label={storyboard.close} className="flex h-9 w-9 items-center justify-center rounded-full border border-paper/30 text-paper/80 transition-colors duration-150 hover:border-mint hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-373" data-mp="55vc33ttjor4">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-374" data-mp="3hjl86zs7j2lt" />
            </button>
          </div>
        </header>

        <div className="flex flex-col gap-7 px-7 py-7" data-id="element-375" data-mp="3mhmojcy90ntl">
          {storyboard.frames.map((frame, i) => <Frame key={frame.id} frame={frame} index={i} data-id="element-376" data-mp="3djb11jsoi65d" data-mpi={i} />)}
          <p className="border-t border-line pt-5 text-[9.5px] uppercase tracking-micro text-mut" data-id="element-377" data-mp="3djkqn69tabaj">
            {storyboard.footer}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}